import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDotLottie } from '@lottiefiles/dotlottie-react';

import { Icons } from 'pages/Calendar/Icons';
import { Row, Col, Tooltip } from 'components-antd';
import { Checkbox } from 'antd';
import { Options } from 'components';
import { NamesList } from '..';

import { getUserId } from 'store/selectors/user';
import { resetTransactionEffect, updateMilestoneStatusEffectAggregated } from 'store/effects';
import { milestoneStatus } from 'settings/constants/milestones';
import { link } from 'settings/navigation/link';
import { Checkmark, Cross } from 'components/Icons';

import dayTileStyles from './../DayView/styles.module.scss';
import styles from './styles.module.scss';
import { formatDate } from 'helpers';

export const MilestoneItem = ({
  title,
  subtitle,
  client,
  agent,
  participants,
  milestoneId,
  isKeyDate,
  isEditable,
  isProject,
  isCreator,
  transactionId,
  status,
  dueDate = null,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isChecked, setIsChecked] = useState(false);
  const [showDummyCheckbox, setShowDummyCheckbox] = useState(status === milestoneStatus.COMPLETE);
  const [isDisable, setIsDisable] = useState(false);
  const userId = useSelector(getUserId);

  useEffect(() => {
    if (participants) {
      const participant = participants.find((participant) => participant.ParticipantId === userId);
      if (!participant) {
        setIsDisable(true);
      } else setIsDisable(!participant?.TransactionAccess);
      if (isKeyDate && !isEditable) {
        setIsDisable(true);
      }
    }
  }, [participants]);

  // Initialize Lottie animation
  const { setCanvasRef, dotLottie } = useDotLottie({
    src: 'https://dusantmc.vercel.app/dotLottie/checkbox2.lottie',
    autoplay: false,
    loop: false,
    speed: 1,
  });

  useEffect(() => {
    if (status === milestoneStatus.COMPLETE) setIsChecked(true);
  }, [status, dotLottie]);

  const onChangeStatus = (checked) => {
    const config = {
      milestoneId,
      status: !checked ? milestoneStatus.INCOMPLETE : milestoneStatus.COMPLETE,
      transactionId: transactionId,
    };

    dispatch(updateMilestoneStatusEffectAggregated(config, { silent: true }));
  };

  const toggleCheckbox = () => {
    setShowDummyCheckbox(false);
    setIsChecked((prev) => {
      const newState = !prev;
      onChangeStatus(newState);
      if (dotLottie) {
        if (newState) {
          dotLottie.play(); // Play full animation
        } else {
          dotLottie.stop(); // Stop animation
          dotLottie.setFrame(0);
        }
      }
      return newState;
    });
  };

  const getTooltipTitle = () => {
    if (isKeyDate && !isEditable) {
      const type = isProject ? 'project' : 'listing/transaction';
      return `This milestone can only be completed when the ${type} disposition is updated accordingly.`;
    } else if (isDisable)
      return `You are not authorized to mark it ${
        status === milestoneStatus.COMPLETE ? 'incomplete.' : 'complete.'
      }`;
    return '';
  };

  const navigateToRoom = () => {
    dispatch(resetTransactionEffect());
    isProject
      ? history.push(link.toProjectOverview(transactionId))
      : history.push(link.toTransactionOverview(transactionId));
  };

  const onCheckboxClick = () => (!isDisable ? toggleCheckbox() : null);

  return (
    <>
      <div
        className={classNames(styles.animatedCheckbox, dayTileStyles?.dayTileItem, {
          [styles.animatedCheckboxChecked]: isChecked,
        })}
      >
        <Row gutter={24} align="middle">
          <Col lg={4}>
            <div className={dayTileStyles.timeline}>
              <div className={classNames(dayTileStyles.bulletPoint, dayTileStyles.milestones)} />
              <span>{dueDate ? formatDate(dueDate, 'M/D/YYYY') : 'All day'}</span>
            </div>
          </Col>
          <Col lg={10}>
            <div className={styles.checkboxContent}>
              {/* Render Lottie animation */}
              <Tooltip
                title={getTooltipTitle()}
                overlayClassName={classNames('mosaikTooltip', styles.canvasTooltip)}
                getPopupContainer={(trigger) => trigger}
                placement="topLeft"
              >
                <div>
                  <canvas
                    ref={setCanvasRef}
                    className={classNames(styles.checkboxIcon, {
                      [styles.hidden]: showDummyCheckbox,
                    })}
                    onClick={onCheckboxClick}
                  />
                </div>
              </Tooltip>
              {isChecked && showDummyCheckbox ? (
                <Tooltip
                  title={getTooltipTitle()}
                  overlayClassName={classNames('mosaikTooltip', styles.milestoneTooltip)}
                  getPopupContainer={(trigger) => trigger}
                  placement="topLeft"
                >
                  <Checkbox
                    checked
                    className={classNames(styles.dummyCheckbox, 'mosaikCheckbox')}
                    onClick={onCheckboxClick}
                  />
                </Tooltip>
              ) : null}
              <div className={styles.checkboxTextContainer}>
                <Options
                  title={
                    <div
                      className={classNames(styles.checkboxText, {
                        [styles.checkboxTextChecked]: isChecked,
                      })}
                    >
                      {title}
                    </div>
                  }
                  placement="bottom"
                  options={[
                    ...(!isDisable
                      ? [
                          {
                            label: `Mark ${isChecked ? 'Incomplete' : 'Complete'}`,
                            icon: isChecked ? (
                              <Cross className={styles.optionCheckmarkIcon} />
                            ) : (
                              <Checkmark className={styles.optionCheckmarkIcon} />
                            ),
                            onClick: onCheckboxClick,
                          },
                        ]
                      : []),
                    {
                      label: `Go to ${isProject ? 'Project' : 'Transaction'}`,
                      icon: <Icons variant={Icons.NAVIGATE} />,
                      onClick: navigateToRoom,
                    },
                  ]}
                  popoverClassName={dayTileStyles.titleOptionsPopover}
                  titleClassName={classNames(dayTileStyles.title, dayTileStyles.titleHover)}
                />

                <div
                  className={classNames(styles.checkboxSubtext, {
                    [styles.checkboxSubtextChecked]: isChecked,
                  })}
                >
                  {subtitle}
                </div>
              </div>
            </div>
          </Col>
          <Col lg={5}>
            {agent?.length > 0 && (
              <div
                className={classNames(dayTileStyles.clientContainer, {
                  [styles.dullText]: isChecked,
                })}
              >
                <div className={dayTileStyles.clientName}>{isProject ? 'Owner' : 'Agent'}:</div>
                <NamesList names={agent} />
              </div>
            )}
          </Col>
          <Col lg={5}>
            {client?.length > 0 && (
              <div
                className={classNames(dayTileStyles.clientContainer, {
                  [styles.dullText]: isChecked,
                })}
              >
                <div className={dayTileStyles.clientName}>Client:</div>
                <NamesList names={client} />
              </div>
            )}
          </Col>
        </Row>
      </div>
      <div className={dayTileStyles.separator} />
    </>
  );
};
